import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Image,
  Download,
  Shield,
  CheckCircle,
  User,
  Plus,
} from 'lucide-react';
import { apiClient } from '@/api/axios';
import { toast } from 'sonner';
import { UploadDocumentModal } from '../documents/UploadDocumentModal';

interface Document {
  id: string;
  name: string;
  category: string;
  uploadDate: string;
  uploadedBy: string;
  uploadedByType: 'owner' | 'professional';
  fileType: string;
  fileSize: number;
  verified: boolean;
  onChainHash?: string;
  issuer?: string;
  notes?: string;
}

interface DocumentTimelineProps {
  petId: string;
}

function decodeBase64UrlToUtf8(base64url: string): string {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

function parseKeyForDisplay(key: string): {
  displayName: string;
  fileName: string;
} {
  const pathParts = key.split('/');
  const lastPart = pathParts[pathParts.length - 1] || '';
  const segs = lastPart.split('__');
  if (segs.length >= 3) {
    try {
      const base64url = segs[1];
      const fileName = segs.slice(2).join('__');
      const displayName = decodeBase64UrlToUtf8(base64url);
      if (displayName && fileName) return { displayName, fileName };
    } catch {
      /* fall through */
    }
  }
  const originalNameIdx = lastPart.indexOf('_');
  const fileName =
    originalNameIdx >= 0 ? lastPart.substring(originalNameIdx + 1) : lastPart;
  return { displayName: fileName, fileName };
}

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  'vaccination-record': 'documents.typeVaccinationCertificate',
  'health-certificate': 'documents.typeHealthCertificate',
  'lab-result': 'documents.typeLabResults',
  'x-ray-scan': 'documents.typeXrayImaging',
  'medical-record': 'documents.typeMedicalRecord',
  'insurance-document': 'documents.typeInsurance',
  grooming: 'documents.typeGroomingReport',
  other: 'documents.typeOther',
};

function getCategoryLabel(
  type: string,
  t: (key: string, defaultValue?: string) => string
): string {
  if (CATEGORY_LABEL_KEYS[type]) {
    return t(CATEGORY_LABEL_KEYS[type]);
  }
  const fallback =
    type
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ') || t('documents.typeOther', 'Other');
  return fallback;
}

export function DocumentTimeline({ petId }: DocumentTimelineProps) {
  const { t } = useTranslation();
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVerification, setFilterVerification] = useState('all');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get<{
        items: {
          key?: string | null;
          name?: string | null;
          size?: number | null;
          lastModified?: string | Date | null;
        }[];
      }>('/documents');
      const items = res.data.items || [];
      const looksLikeSocial = (value: string): boolean => {
        const k = value.replace(/\\/g, '/').trim().toLowerCase();
        if (
          k.startsWith('social_') ||
          k.includes('/social_') ||
          k.includes('/social/')
        )
          return true;
        const parts = value.split('/');
        const lastSegment = (parts[parts.length - 1] || '').toLowerCase();
        if (lastSegment.startsWith('social_')) return true;
        if (lastSegment.includes('_social_')) return true;
        return parts.some(
          (p) =>
            p.toLowerCase() === 'social' ||
            p.toLowerCase().startsWith('social_')
        );
      };
      const documentItems = items.filter((i) => {
        if (!i.key || typeof i.key !== 'string') return false;
        if (i.key.includes('/pets/')) return false;
        if (looksLikeSocial(i.key)) return false;
        if (i.name && typeof i.name === 'string' && looksLikeSocial(i.name))
          return false;
        return true;
      });
      const mapped: Document[] = documentItems
        .filter((i) => i.key)
        .map((i) => {
          const key = i.key as string;
          const pathParts = key.split('/');
          const type = pathParts.length > 1 ? pathParts[1] : 'other';
          const { displayName, fileName } = parseKeyForDisplay(key);
          const lastModified = i.lastModified
            ? new Date(i.lastModified as string)
            : new Date();
          return {
            id: key,
            name:
              displayName ||
              fileName ||
              t('documents.unnamedDocument', 'Document'),
            category: getCategoryLabel(type, t),
            uploadDate: lastModified.toISOString(),
            uploadedBy: t('common.owner', 'Owner'),
            uploadedByType: 'owner',
            fileType: fileName?.toLowerCase().endsWith('.pdf')
              ? 'application/pdf'
              : 'image/jpeg',
            fileSize: (i.size as number) || 0,
            verified: false,
          };
        });
      // Exclude any that display as social (display name starts with social_)
      const filteredMapped = mapped.filter((doc) => {
        const n = (doc.name || '').toLowerCase();
        const id = (doc.id || '').toLowerCase();
        return !n.startsWith('social_') && !id.startsWith('social_');
      });
      filteredMapped.sort(
        (a, b) =>
          new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
      );
      setDocuments(filteredMapped);
    } catch (e) {
      console.error('Failed to fetch documents', e);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [petId]);

  const categories = [
    'all',
    ...Array.from(new Set(documents.map((d) => d.category))).sort(),
  ];

  const filteredDocuments = documents.filter((doc) => {
    if (filterCategory !== 'all' && doc.category !== filterCategory)
      return false;
    if (filterVerification === 'verified' && !doc.verified) return false;
    if (filterVerification === 'unverified' && doc.verified) return false;
    return true;
  });

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/'))
      return <Image className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    return <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getVerificationBadge = (doc: Document) => {
    if (doc.onChainHash) {
      return (
        <span
          className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 px-2 py-1 bg-purple-50 dark:bg-purple-900/30 rounded"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <Shield className="w-3 h-3" />
          {t('documents.badgeOnChain', 'On-chain certified')}
        </span>
      );
    }
    if (doc.verified) {
      return (
        <span
          className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 px-2 py-1 bg-green-50 dark:bg-green-900/30 rounded"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <CheckCircle className="w-3 h-3" />
          {t('documents.badgeProfessionalVerified', 'Verified by professional')}
        </span>
      );
    }
    return (
      <span
        className="flex items-center gap-1 text-xs text-gray-600 dark:text-white/70 px-2 py-1 bg-gray-50 dark:bg-white/10 rounded"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <User className="w-3 h-3" />
        {t('documents.badgeOwnerUploaded', 'Uploaded by owner')}
      </span>
    );
  };

  const handleDownload = async (doc: Document) => {
    try {
      const encodedKey = encodeURIComponent(doc.id);
      const res = await apiClient.get<{ url: string; filename?: string }>(
        `/documents/${encodedKey}/download`,
        { params: { disposition: 'attachment' } }
      );
      const a = document.createElement('a');
      a.href = res.data.url;
      a.download = res.data.filename || doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(t('documents.downloadStarted', 'Download started'));
    } catch (err) {
      console.error('Download failed', err);
      toast.error(t('documents.downloadFailed', 'Download failed'));
    }
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    fetchDocuments();
    toast.success(t('documents.documentUploaded'));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3
            className="text-xl mb-1 text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('documents.documentTimelineTitle')}
          </h3>
          <p
            className="text-sm text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('documents.documentTimelineSubtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
          style={{
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
          }}
        >
          <Plus className="w-4 h-4" />
          {t('documents.upload')}
        </button>
      </div>

      {loading ? (
        <div
          className="text-center py-12 text-gray-500 dark:text-white/50"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t('documents.loadingDocuments')}
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-[#0F0E16] rounded-xl border border-gray-200 dark:border-white/8">
          <FileText className="w-16 h-16 text-gray-300 dark:text-white/20 mx-auto mb-4" />
          <p
            className="text-gray-600 dark:text-white/70 mb-2"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('documents.noDocuments')}
          </p>
          <p
            className="text-sm text-gray-500 dark:text-white/50 mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('documents.noDocumentsGetStarted')}
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity"
            style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
            }}
          >
            <Plus className="w-4 h-4" />
            {t('documents.upload')}
          </button>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div>
              <label
                className="block text-sm text-gray-600 dark:text-white/70 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.filterByCategory')}
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? t('documents.allCategories') : cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-sm text-gray-600 dark:text-white/70 mb-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.filterByVerification')}
              </label>
              <select
                value={filterVerification}
                onChange={(e) => setFilterVerification(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <option value="all">{t('documents.allDocuments')}</option>
                <option value="verified">{t('documents.verifiedOnly')}</option>
                <option value="unverified">
                  {t('documents.unverifiedOnly')}
                </option>
              </select>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.totalDocuments')}
              </div>
              <div
                className="text-2xl text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {documents.length}
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.verified')}
              </div>
              <div
                className="text-2xl text-green-600 dark:text-green-400"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {documents.filter((d) => d.verified || d.onChainHash).length}
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.ownerUploaded')}
              </div>
              <div
                className="text-2xl text-blue-600 dark:text-blue-400"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {documents.filter((d) => !d.verified && !d.onChainHash).length}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-16 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-white/10" />
            <div className="space-y-6">
              {filteredDocuments.map((doc) => (
                <div key={doc.id} className="relative pl-24">
                  <div className="absolute left-[59px] top-6 w-4 h-4 rounded-full bg-blue-600 dark:bg-blue-500 border-4 border-white dark:border-[#16151D]" />
                  <div
                    className="absolute left-0 top-5 text-xs text-gray-500 dark:text-white/50 w-12"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      lineHeight: '1.2',
                    }}
                  >
                    {new Date(doc.uploadDate).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-4 hover:shadow-md dark:hover:bg-[#1A1921] transition-shadow">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {getFileIcon(doc.fileType)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <h4
                              className="truncate mb-1 text-gray-900 dark:text-white"
                              style={{
                                fontFamily: 'Sora, sans-serif',
                                fontWeight: 600,
                              }}
                            >
                              {doc.name}
                            </h4>
                            <div
                              className="flex items-center gap-2 text-sm text-gray-600 dark:text-white/70 mb-1"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              <span className="px-2 py-0.5 bg-gray-100 dark:bg-white/10 rounded text-xs">
                                {doc.category}
                              </span>
                              <span>•</span>
                              <span>{formatFileSize(doc.fileSize)}</span>
                            </div>
                          </div>
                          {getVerificationBadge(doc)}
                        </div>
                        <div
                          className="text-xs text-gray-500 dark:text-white/50 mb-2"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {t('documents.uploadedBy')} {doc.uploadedBy} •{' '}
                          {new Date(doc.uploadDate).toLocaleString()}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button
                            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-white/20 rounded hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-gray-700 dark:text-white/90"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                            onClick={() => handleDownload(doc)}
                          >
                            <Download className="w-4 h-4" />
                            {t('documents.download')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {filteredDocuments.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 dark:text-white/20 mx-auto mb-4" />
              <p
                className="text-gray-500 dark:text-white/50"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('documents.noDocumentsMatching')}
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-6">
            <h4
              className="mb-3 text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {t(
                'documents.verificationBadgesTitle',
                'Verification badge meanings'
              )}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-3">
                <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5" />
                <div style={{ fontFamily: 'Inter, sans-serif' }}>
                  <strong className="text-purple-600 dark:text-purple-400">
                    {t('documents.onChainTitle', 'On-chain certified:')}
                  </strong>{' '}
                  <span className="text-gray-700 dark:text-blue-100">
                    {t(
                      'documents.onChainDesc',
                      'Document hash is stored on-chain and can be independently verified.'
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5" />
                <div style={{ fontFamily: 'Inter, sans-serif' }}>
                  <strong className="text-green-600 dark:text-green-400">
                    {t(
                      'documents.professionalVerifiedTitle',
                      'Professionally verified:'
                    )}
                  </strong>{' '}
                  <span className="text-gray-700 dark:text-blue-100">
                    {t(
                      'documents.professionalVerifiedDesc',
                      'Uploaded by a verified professional (vet, groomer, etc.).'
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-gray-600 dark:text-white/70 mt-0.5" />
                <div style={{ fontFamily: 'Inter, sans-serif' }}>
                  <strong className="text-gray-600 dark:text-white/90">
                    {t('documents.ownerUploadedTitle', 'Owner uploaded:')}
                  </strong>{' '}
                  <span className="text-gray-700 dark:text-blue-100">
                    {t(
                      'documents.ownerUploadedDesc',
                      'Uploaded by the owner and can be verified by a professional later.'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showUploadModal && (
        <UploadDocumentModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadComplete}
        />
      )}
    </div>
  );
}
